import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { IPropertyDataDetails, Tempo } from "types/services";
import { PropertyMlsStatus } from "./PropertyCard/MLSStatus";
import { formatAmountIntoUSD, getPropertyTypeClass, getPropertyTypeText, isEmpty, joinWithDot, parseCompositeHarid, resolveListingPrice, twoArraysToObject } from "utils/helpers";
import { fetchWrapper } from "services/api-client/fetchWrapper";
import { captureException } from "scripts/logging/error-report";
import { Alert } from "react-bootstrap";
import { useSetTimeout } from "scripts/hooks/timers";
import { useLock } from "scripts/hooks/async";
import { useBookmarkContext } from "services/BookmarkContext";
import { Highlights } from "./Highlights";
import { ExteriorOrInteriorOrRoomCard } from "./ExteriorOrInteriorOrRoomCard";
import { useUserContext } from "services/UserContext";
import { AgentInfoCard } from "./AgentInfoCard";
import { websiteProductionHost } from "scripts/application/setting";
import { trackEvent } from "services/api-client/matomoTracker";
import { useQueryFlags } from "scripts/hooks/useQueryFlags";

export interface IPropertyPopUpProps {
  harid: string | number;
  id: string;
  statusShort: string;
  onClose?(): void;
}

export function PropertyPopUp(props: IPropertyPopUpProps) {
  const { user } = useUserContext();
  const { bookmark, setBookmark } = useBookmarkContext();
  const { embed, search } = useQueryFlags();
  const [isLoading, setLoading] = useState(true);
  const { setTimeout } = useSetTimeout();
  const { harid, statusShort, id } = props;
  const [propertyData, setPropertyData] = useState<IPropertyDataDetails>();

  // When embedded, drop embed-only flags so the new tab loads the full AI
  // Mode UI; cid is kept since it identifies the connected agent.
  const chatLinkSearch = (() => {
    if (!embed) return "";
    const params = new URLSearchParams(search);
    ["embed", "source", "hide_user", "hide_header", "hide_footer", "hide_share", "timeline"].forEach(
      (key) => params.delete(key)
    );
    const qs = params.toString();
    return qs ? `?${qs}` : "";
  })();

  // Track bookmark changes without triggering context updates immediately
  const pendingBookmarkUpdate = useRef<string[] | null>(null);

  const [showAlertBookmark, setShowAlertBookmark] = useState({
    show: false,
    message: "",
    variant: "success",
  });
  const realtor = propertyData?.realtor;
  const broker = propertyData?.broker;

  const connectedAgent = user?.connected_agent;
  const isBrandedApp = !!(user.role === "consumer" && connectedAgent);
  const nameOrEmail = `${connectedAgent?.firstname} ${connectedAgent?.lastname}` || connectedAgent?.email || '';

  const beds =
    !isEmpty(propertyData?.detailItems?.Bedrooms) &&
    `${propertyData?.detailItems.Bedrooms} bed${propertyData?.detailItems.Bedrooms == "1" ? "" : "s"
    }`;
  const bath =
    !isEmpty(propertyData?.detailItems?.Baths) &&
    `${propertyData?.detailItems.Baths} bath${propertyData?.detailItems.Baths == "1" ? "" : "s"
    }`;
  const stories = !isEmpty(propertyData?.detailItems?.Stories)
    ? `${propertyData?.detailItems?.Stories} ${propertyData?.detailItems.Stories == "1" ? "story" : "stories"
    }`
    : "";
  const buildingSQFT =
    !isEmpty(propertyData?.detailItems?.["Building SQFT"]) &&
    `${propertyData?.detailItems["Building SQFT"]} Building Sqft`;
  const priceSQFT =
    propertyData?.detailItems?.["Price per SQFT"] &&
      !isEmpty(propertyData.detailItems["Price per SQFT"])
      ? `$${Math.round(
        parseFloat(
          propertyData.detailItems["Price per SQFT"].replace("$", "")
        ) || 0
      )}/Sqft.`
      : "";

  const yearBuiltin =
    !isEmpty(propertyData?.detailItems?.["Year Built"]) &&
    `${propertyData?.detailItems["Year Built"]} year built`;
  const details =
    joinWithDot(
      propertyData?.detailItems?.["Property Type"],
      beds,
      bath,
      stories,
      buildingSQFT,
      priceSQFT,
      yearBuiltin
    );
  const image =
    propertyData?.photos && propertyData?.photos?.urls?.length > 0
      ? propertyData?.photos.urls[0]
      : "https://content.harstatic.com/media/icons/artwork/no_photo_placeholder__large.svg";
  //const days=propertyData?.detail&&differenceInDays(new Date(),propertyData?.detail.date);
  const days = propertyData?.doh;
  const listingStatus = propertyData?.detailItems?.Status ?? "";
  const typeClass = getPropertyTypeClass(
    propertyData?.detail.type ?? "",
    listingStatus
  );
  const typetext = getPropertyTypeText(
    propertyData?.detail.type ?? "",
    listingStatus
  );
  const showType = propertyData?.detail.status !== "Sold";
  const isTypeOnNextLine = (propertyData?.status_text.length ?? 0) > 10;
  const bookmarked = propertyData?.bookmarked == 1;
  // const isLoggedin = propertyData?.isLogin == 1;
  const isLoggedin = user?.role !== "guest";
  const propertyHavingHighlights =
    propertyData?.highlights && propertyData?.highlights.length > 0;
  const description = propertyData?.carmode?.description
  // Sold listings show what they sold for, not what they were listed at - and
  // only if this user is allowed to see the exact figure.
  const displayPrice = resolveListingPrice(
    propertyData?.detail?.sold_price_display,
    propertyData?.detail?.price
  );

  const handleGetPropertyDetails = async (signal?: AbortSignal) => {
    try {
      const { data } = await fetchWrapper.get(
        `/api/v1/chatbot/property-details/${harid}?mlsInfo=1${statusShort ? `&status=${statusShort}` : ""}${id ? `&id=${id}` : ""}`,
        signal
      );
      const detailitems = twoArraysToObject(
        data.detail.detailitems.titles,
        data.detail.detailitems.values
      );
      !!data && setPropertyData({ ...data, detailItems: detailitems });
      setLoading(false);
      trackEvent(`AI Mode / Property Pop Up / ${harid}`);
    } catch (e) {
      captureException(e);
    }
  };

  const handleBookmark = useLock(async () => {
    try {
      const { message } = !bookmarked
        ? await fetchWrapper.post(
          `/api/v1/chatbot/bookmark/${propertyData?.mlsnum}`,
          {}
        )
        : await fetchWrapper._delete(
          `/api/v1/chatbot/bookmark/${propertyData?.mlsnum}`
        );
      setShowAlertBookmark({ show: true, message: message, variant: !bookmarked ? "success" : "danger" });
      propertyData &&
        setPropertyData({ ...propertyData, bookmarked: bookmarked ? 0 : 1 });

      // Store the bookmark change in ref instead of updating context immediately
      // This prevents parent component re-render which would close the popup
      const bookmarks = bookmarked
        ? bookmark?.mlsnums.filter((item) => item !== propertyData?.mlsnum)
        : [...(bookmark?.mlsnums ?? []), propertyData?.mlsnum ?? ""];
      pendingBookmarkUpdate.current = bookmarks ?? [];
      const event = bookmarked ? "Remove Bookmark" : "Bookmark";
      trackEvent(`AI Mode / Property Pop Up ${event} / ${harid}`);

      setTimeout(
        () => setShowAlertBookmark({ show: false, message: "", variant: "success" }),
        5000
      );
    } catch (e) {
      captureException(e);
      console.error(e);
    }
  });

  useEffect(() => {
    const aborter = new AbortController();
    handleGetPropertyDetails(aborter.signal);

    // Cleanup: sync bookmark changes to context when popup unmounts/closes
    return () => {
      aborter.abort();
      if (pendingBookmarkUpdate.current !== null) {
        setBookmark({ mlsnums: pendingBookmarkUpdate.current });
      }
    };
  }, [harid, setBookmark]);

  const onOverlayClick = (e: React.MouseEvent) => {
    if (e.target !== e.currentTarget) {
      return;
    }
    e.preventDefault();
    props.onClose?.();
  };

  const agentView = propertyData?.agent_view;
  const tempoLinks = agentView?.links?.tempo || []
  // const harLinks = agentView?.links?.har || []
  const harLinks = (agentView?.links?.har || []).filter(link => link?.["short-title"] !== "DOC") as Tempo[];
  // const CMALink = harLinks.find(link => link?.["short-title"] === "CMA") as Tempo;

  const agentViewLinks = agentView ? [
    ...tempoLinks,
    ...harLinks,
    // CMALink,

    {
      active: 1,
      displaytext: "Home Value Comparables (AVM)",
      url: `https://www.har.com/homevalues/avm_detail?mlsnum=${propertyData?.mlsnum}`,
    },
    {
      active: 1,
      displaytext: agentView?.extra?.additional?.name,
      url: propertyData?.share_url + "#AData-mls",
    },
    {
      active: 1,
      displaytext: agentView?.extra?.finance?.name,
      url: propertyData?.share_url + "#FData-mls",
    },
    {
      active: 1,
      displaytext: agentView?.extra?.contacts?.name,
      url: propertyData?.share_url + "#CData-mls",
    },
    {
      active: 1,
      displaytext: "FEMA Flood Map",
      url: `https://msc.fema.gov/portal/search?AddressQuery=${propertyData?.detail?.gps[1]}, ${propertyData?.detail?.gps[0]}`,
    },
    {
      active: 1,
      displaytext: "Request appointment",
      url: `https://www.har.com/ws/showing_request/${propertyData?.mlsnum}`,
    },
  ] : [];

  const checkValueIsTruthy = (value: any) => {
    return Array.isArray(value) ? (value || [])?.length > 0 : Object.keys(value || {}).length > 0;
  }
  const hasOpenHouse = (propertyData?.openhouse || [])?.length > 0;

  const isForSaleButtonHidden = ["CLOSD", "LEASED", "EXP", "WITH", "TERM"].includes(propertyData?.status_short ?? "");
  const parsedHarid = parseCompositeHarid(propertyData?.harid);
  const galleryUrl = parsedHarid
    ? `${websiteProductionHost}/home-gallery/${parsedHarid}`
    : propertyData?.share_url;

  return (
    <div className="property_overlay d-block" onClick={onOverlayClick}>
      <div className="property_window max-width-500px">
        {(isLoading || !propertyData) && (
          <div style={{ width: '100%', height: "92.5vh" }} className="center">
            {isLoading ? (
              <>
                <div className="text-center border_radius--pudgy bg_color_cement_light p-4 ml-auto mr-auto" style={{ maxWidth: '450px' }}>
                  <div className="d-flex align-items-center">
                    <div className="spinner-border mr-4 color_har_blue_dark" style={{ width: '2rem', height: '2rem' }} role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                    <div className="font_size--medium color_auxiliary">Loading Property Details...</div>
                  </div>
                </div>
              </>
            ) : (
              <span className="font_weight--bold font_size--medium ml-2">Loading Property Details...</span>
            )}
          </div>
        )}
        {propertyData && (
          <div>
            <div className="pw_header d-flex mb-1" style={{ borderBottom: statusShort === "A" && isLoggedin ? "1px solid #eee" : "none" }}>
              <div className="d-flex pw-header_btns">
                {statusShort === "A" && isLoggedin && (
                  <a
                    onClick={handleBookmark}
                    className={`cursor--pointer btn btn--icon--borderless btn--icon cmd--fav ${bookmarked ? 'btn--icon--favorited' : 'btn--icon--favorite'
                      } mr-3`}
                    style={{ width: 'auto' }}
                    title={bookmarked ? 'Remove bookmark' : 'Add bookmark'}
                  />
                )}
              </div>
              <div className="btn_close_popup close_prop_win cursor--pointer">
                <img
                  onClick={onOverlayClick}
                  src="https://content.harstatic.com/media/icons/close_black.svg"
                />
              </div>
            </div>
            <div className={`mx-3 mb-3${statusShort === "A" && isLoggedin ? ' mt-3' : ''}`}>
              {isBrandedApp && connectedAgent ? (
                <AgentInfoCard
                  agentProfileUrl={connectedAgent?.bioUrl}
                  brokerProfileUrl={connectedAgent?.office_url || ''}
                  name={nameOrEmail}
                  photo={connectedAgent?.photo}
                  officeName={connectedAgent?.office_name}
                  mlsPlantinum={connectedAgent?.is_platinum}
                />
              ) : (
                <AgentInfoCard
                  agentProfileUrl={realtor?.biourl}
                  brokerProfileUrl={broker?.officeurl || ''}
                  name={realtor?.agentname || ''}
                  photo={realtor?.photo || ''}
                  officeName={broker?.officename || ''}
                  mlsPlantinum={realtor?.mls_plantinum}
                />
              )}
            </div>
            <div>
              <a
                href={galleryUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View property gallery in new tab"
              >
                <div
                  className="border_radius--default bg--image mx-3"
                  style={{
                    backgroundImage: `url('${image}')`,
                    paddingBottom: "60%",
                    position: "relative",
                  }}
                >
                  {(propertyData?.photos?.urls || [])?.length > 0 && (
                    <a href={galleryUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="btn btn--ordinary btn--medium openGallery d-flex align-items-center " data-media-type="photo-Tab" style={{ padding: "0px 12px", position: "absolute", bottom: "10px", right: "10px" }}>
                      <img className="mr-2 d-md-block d-none" style={{ width: "22px" }} src="https://content.harstatic.com/media/icons/a_all/photos_icon_black.svg" alt="gallery-icon" />
                      {propertyData.photos.urls.length || 0} photos
                    </a>
                  )}
                </div>

              </a>
              <div className="border-bottom border-color--cloudy-sky-light m-3 pb-3">
                <div className="row align-items-center">
                  <div className="col">
                    <div
                      className="font_size--large_extra font_weight--bold font_weight--bold_extra color_carbon mb-0"
                      tabIndex={0}
                    >
                      {displayPrice.value}
                      {displayPrice.sqft && (
                        <span className="font_size--small font_weight--regular ml-1">
                          {displayPrice.sqft}
                        </span>
                      )}

                      <div className="d-inline-flex align-items-center price_labels pl-2">
                        {!isTypeOnNextLine && showType && !isForSaleButtonHidden && (
                          <div className={`label no-wrap label--${typeClass} font-weight-normal`}>
                            {typetext}
                          </div>
                        )}
                        <PropertyMlsStatus
                          mls_status_text={propertyData.detailItems.Status}
                        />

                        {isTypeOnNextLine && showType && !isForSaleButtonHidden && (
                          <div className={`label no-wrap label--${typeClass} font-weight-normal`}>
                            {typetext}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-auto ml-auto">
                    <span className="bg_color_cloudy_sky_light border_radius--round font_size--small px-2 py-1 mr-1">
                      {days}
                    </span>
                    <span>
                      Day{parseInt(days ?? "0") > 1 ? "s" : ""} on HAR
                    </span>
                  </div>
                </div>

                <div className="d-flex align-items-center pt-2">
                  <div className="mr-1 font_size--medium font_weight--semi_bold">
                    {propertyData.detail.address}, {" "}
                    {propertyData.detail.city}, {propertyData.detail.state}{" "}
                    {propertyData.detail.zip}
                  </div>
                  <a
                    href={`https://www.har.com/directions?address=${propertyData?.mlsnum}`}
                    target="_blank"
                    className=" font_size--medium"
                  >
                    Directions
                  </a>
                  {days && (
                    <div className="ml-auto">
                      <a
                        href={`${propertyData.share_url}#calculatorInfo`}
                        target="_blank"
                        className="d-inline-flex ml-auto font_size--medium ml-auto"
                      >
                        <span className="mr-2 flex-nowrap text-nowrap pl-3">See more</span>
                        <img src="https://content.harstatic.com/media/icons/arrow-right.svg" alt="right-arrow-icon" />
                      </a>
                    </div>
                  )}
                </div>
              </div>
              <div className="border-bottom border-color--cloudy-sky-light m-3 pb-3">
                <div>
                  {!!propertyData.mortgage?.principalinterest && (
                    <div className="font_size--medium">
                      Est. Mortgage{" "}
                      {formatAmountIntoUSD(
                        Math.round(
                          parseFloat(propertyData.mortgage.principalinterest)
                        )
                      )}
                      /month
                    </div>
                  )}
                </div>
              </div>
              <div className="font_size--large px-3 pb-3">{details}</div>
              {(hasOpenHouse || propertyData?.just_listed === "1" || propertyData?.newconstruction === "1" || propertyData?.foreclosure === "1") && (
                <div className="d-flex flex-wrap mx-3 mb-4">
                  {hasOpenHouse && (
                    <div className="label label--openhouse_simple mb-2 mr-2 no-wrap">
                      <img src="https://content.harstatic.com/media/icons/icon-open-house.svg" />
                      Open House
                    </div>
                  )}
                  {propertyData.just_listed === "1" && (
                    <div className="label label--justlisted mb-2 mr-2">
                      <img src="https://content.harstatic.com/media/icons/icon-just-listed.svg" />
                      Just listed
                    </div>
                  )}
                  {propertyData.newconstruction === "1" && (
                    <div className="label label--newconstruction mb-2 mr-2">
                      <img src="https://content.harstatic.com/media/icons/icon-new-construction.svg" />
                      New construction
                    </div>
                  )}
                  {propertyData.foreclosure === "1" && (
                    <div className="label label--foreclosure mb-2 mr-2">
                      <img src="https://content.harstatic.com/media/icons/icon-foreclosure.svg" />
                      Foreclosure
                    </div>
                  )}
                </div>
              )}
              {propertyData?.detail?.pricereduced == "Y" && (
                <div
                  className="priceReducedContent p-3 mx-3 mb-3 border_radius--pudgy"
                  style={{ border: "1px solid rgb(168, 0, 195) !important" }}
                >
                  <div className="row no-gutters">
                    {
                      <div className="col font_size--medium">
                        <div className="label label--pricereduction">
                          <img src="https://content.harstatic.com/media/icons/icon-price-reduction.svg" />
                          Price Change
                        </div>
                      </div>
                    }
                    <div className="col-auto pt-1 font_size--medium">
                      <div className="font_size--small font_weight--regular">
                        Last reduced: {propertyData.detail.lastreduceddate}
                      </div>
                    </div>
                  </div>
                  <div className="row no-gutters">
                    <div className="col">
                      <div className="font_size font_size--medium mt-3">
                        Reduction of
                      </div>
                      {!!propertyData.detail.pricereducedamt &&
                        !!propertyData.detail.reducedpercent && (
                          <div className="font_size font_size--large font_weight font_weight--semi_bold  mt-1">
                            $
                            {parseInt(
                              propertyData.detail.pricereducedamt
                            ).toLocaleString()}{" "}
                            (
                            {parseFloat(propertyData.detail.reducedpercent).toFixed(
                              2
                            )}
                            %)
                          </div>
                        )}
                    </div>
                    <div className="col-auto text-right">
                      <div className="font_size font_size--medium mt-3">
                        Previous Price
                      </div>
                      {!!propertyData.detail.originalprice && (
                        <div className="font_size font_size--large font_weight font_weight--semi_bold  mt-1">
                          $
                          {parseInt(
                            propertyData.detail.originalprice
                          ).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div className="mx-3">
                {(propertyHavingHighlights || description) && (
                  <div className="pt-4 border-top border-color--cloudy-sky-light">
                    <h3 className="mb-3 font-weight-bold">About this property</h3>
                    {propertyHavingHighlights && (
                      <div>
                        <Highlights highlights={propertyData?.highlights} />
                      </div>
                    )}
                    <p className="font_size--medium mb-3">
                      {description || "No description available."}
                    </p>
                  </div>
                )}
                {propertyData?.subdivision_name && (
                  <div className="mb-3 mt-2">
                    <div className="font_weight--bold font_size--small_extra">Subdivision</div>
                    <div className="font_size--large text-break text-uppercase">{propertyData.subdivision_name}</div>
                  </div>
                )}
                {checkValueIsTruthy(propertyData?.exterior) && (
                  <div className="pt-4 border-top border-color--cloudy-sky-light">
                    <h3 className="font-weight-bold mb-3">Exterior</h3>
                    <ExteriorOrInteriorOrRoomCard data={propertyData?.exterior} columns={'2'} />
                  </div>
                )}
                {checkValueIsTruthy(propertyData?.rooms) && (
                  <div className="pt-4 border-top border-color--cloudy-sky-light">
                    <h3 className="font-weight-bold mb-3">Rooms</h3>
                    <ExteriorOrInteriorOrRoomCard data={propertyData?.rooms} columns={'2'} />
                  </div>
                )}
                {checkValueIsTruthy(propertyData?.interior) && (
                  <div className="pt-4 border-top border-color--cloudy-sky-light">
                    <h3 className="font-weight-bold mb-3">Interior</h3>
                    <ExteriorOrInteriorOrRoomCard data={propertyData?.interior} columns={'2'} />
                  </div>
                )}
              </div>
            </div>
            <div
              className="position-sticky bg_color_snow_white property_overlay_footer"
              style={{ bottom: 0 }}
            >
              {showAlertBookmark.show && (
                <Alert
                  className="mb-4"
                  show={showAlertBookmark.show}
                  variant={showAlertBookmark.variant}
                >
                  {showAlertBookmark.message}
                </Alert>
              )}
              <div className="row small-gutters">
                <div className="col-6">
                  <div className="btn-group view-details-group-button w-100" role="group" aria-label="Button group with nested dropdown">
                    <a
                      type="button"
                      href={propertyData.share_url}
                      target="_blank"
                      className="btn btn--ordinary btn--large w-100"
                    >
                      View Details
                    </a>
                    {agentView && (
                      <div className="btn-group" role="group">
                        <button type="button" className="btn btn-secondary dropdown-toggle" data-toggle="dropdown" aria-expanded="false" />
                        {agentViewLinks?.length > 0 && (
                          <div className="dropdown-menu">
                            {agentViewLinks?.map((link, index) => {
                              // if (link?.active) {
                              return (
                                <a key={index} className="dropdown-item font_size--medium py-2" href={link.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {link?.displaytext}
                                </a>
                              )
                              // }
                              // return null;
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-6">
                  <Link
                    to={`/listing/${harid}${chatLinkSearch}`}
                    target={embed ? "_blank" : undefined}
                    rel={embed ? "noopener noreferrer" : undefined}
                    className="btn btn--primary btn--large w-100"
                    onClick={() => trackEvent(`AI Mode / Property Pop Up Chat / ${harid}`)}
                  >
                    <img src="https://content.harstatic.com/media/icons/a_all/chat_ai_icon2.svg" className="ai-mode-white" alt="AI Mode Icon" />
                    AI Mode Chat
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div >
  );
}

